import React, {Fragment} from 'react';
import {useQuery} from "@apollo/client";
import {useParams, Link} from 'react-router-dom'
import parse, {domToReact} from 'html-react-parser';
import SlickSlider from 'react-slick';
import {T} from '../../../utils/I18n/I18n';
import {BLOG_POST, LATEST_BLOG_POSTS, BLOG_TAGS} from "../queriesBlog";
import NewestBlogItem from "../NewestBlogItem";
import Loader from '../../../components/Loader/Loader';
import ProductItem from '../../../components/Category/ProductItem/ProductItem';
import useHydrationDelay from '../../../hooks/useHydrationDelay';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import '../../../renderComponents/ProductsSlider/ProductSlider.scss';

import '../Blog.scss';
import './BlogDetails.scss';
import '../../../components/Category/Breadcrumbs/Breadcrumbs.scss';
import { Helmet } from 'react-helmet-async';

const getText = node => {
    if (node.type === 'text') {
        return node.data || '';
    }

    return node.children?.map(getText).join('') || '';
};

const findNode = (node, predicate) => {
    if (predicate(node)) {
        return node;
    }

    for (const child of node.children || []) {
        const match = findNode(child, predicate);
        if (match) {
            return match;
        }
    }

    return null;
};

const findNodes = (node, predicate) => [
    ...(predicate(node) ? [node] : []),
    ...(node.children || []).flatMap(child => findNodes(child, predicate)),
];

const getAttribute = (node, attribute) => findNode(node, item => item.attribs?.[attribute])?.attribs?.[attribute];

const getBlogProduct = node => {
    const sku = getAttribute(node, 'data-product-sku');
    const nameNode = findNode(node, item => item.attribs?.class?.includes('product-item-name'));
    const imageNode = findNode(node, item => item.attribs?.class?.includes('product-image-photo'));
    const linkNode = findNode(node, item => item.attribs?.class?.includes('product-item-photo'));

    if (!sku || !nameNode || !imageNode || !linkNode) {
        return null;
    }

    const priceNodes = findNodes(node, item => item.attribs?.['data-price-amount']);
    const prices = priceNodes.map(priceNode => ({
        amount: Number(priceNode.attribs['data-price-amount']),
        currency_symbol: '€',
        price_type: priceNode.attribs['data-price-type'] === 'finalPrice' ? 'simple_regular' : 'simple_clearance',
    }));

    return {
        __typename: 'SimpleProduct',
        name: getText(nameNode).trim(),
        sku,
        image: imageNode.attribs.src,
        thumbnailImage: {url: imageNode.attribs.src},
        url: linkNode.attribs.href?.replace(/^https?:\/\/[^/]+/, ''),
        canonical_url: linkNode.attribs.href?.replace(/^https?:\/\/[^/]+/, ''),
        prices,
        isHasStock: true,
        isOnOrder: false,
        guarantees: [],
        emlabels: [],
    };
};

const BlogProductCarousel = ({productNodes}) => {
    const isHydrated = useHydrationDelay();
    const productsList = productNodes.find(child => child.name === 'ol');
    const products = productsList?.children?.filter(child => child.name === 'li') || [];
    const productData = products.map(getBlogProduct).filter(Boolean);

    if (!isHydrated) {
        return <div className="blog-product-carousel-placeholder" aria-hidden="true" />;
    }

    if (!productData.length) {
        return <>{domToReact(productNodes)}</>;
    }

    return (
        <div className="product-slider blog-product-carousel">
            <SlickSlider
                arrows
                dots
                infinite={productData.length > 1}
                slidesToShow={Math.min(4, productData.length)}
                slidesToScroll={Math.min(4, productData.length)}
                responsive={[
                    {breakpoint: 1100, settings: {slidesToShow: Math.min(3, productData.length), slidesToScroll: Math.min(3, productData.length)}},
                    {breakpoint: 918, settings: {slidesToShow: Math.min(2, productData.length), slidesToScroll: Math.min(2, productData.length)}},
                    {breakpoint: 550, settings: {slidesToShow: 1, slidesToScroll: 1}},
                ]}
            >
                {productData.map(product => (
                    <div key={product.sku} className="product-slide-wrapper">
                        <ProductItem product={product} isCms />
                    </div>
                ))}
            </SlickSlider>
        </div>
    );
};

const replaceBlogContentNode = domNode => {
    if (domNode.attribs?.['data-content-type'] === 'products' && domNode.attribs?.['data-appearance'] === 'carousel') {
        return <BlogProductCarousel productNodes={domNode.children} />;
    }

    return undefined;
};

const BlogDetails = () => {
    const params = useParams();

    const {data, loading} = useQuery(BLOG_POST, {
        variables: { identifier: params.blogId },
        fetchPolicy: "cache-first"
    })
    const {data: latestPostsData, loading: load} = useQuery(LATEST_BLOG_POSTS, {
        fetchPolicy: "cache-first"
    });
    const {data: blogTagsData, loading: lo} = useQuery(BLOG_TAGS, {
        fetchPolicy: "cache-first"
    });

    return <Fragment>
        {data && data.blogPost && (
            <Helmet>
                <title>{data.blogPost.title}</title>
                {data.blogPost.og_title && <meta property="og:title" content={data.blogPost.og_title} />}
                {data.blogPost.og_description && <meta property="og:description" content={data.blogPost.og_description} />}
                <meta property="og:type" content={data.blogPost.og_type || 'article'} />
                {data.blogPost.og_image && <meta property="og:image" content={data.blogPost.og_image} />}
            </Helmet>
        )}
        <div className="blog">
            <div className="blog__breadcrumbs">
                <div className="breadcrumbs">
                    <Link className="breadcrumbs__link" to="/">
                        Naslovna
                    </Link>
                    <Link className="breadcrumbs__link" to="/blog"><T id="blog.title" /></Link>
                    <span>{!loading && data?.blogPost?.title}</span>
                </div>
            </div>

            {loading && <Loader />}

            <main className="blog__main-content">

                <div className="blog__column">

                    <div className="blog-details__column-main">
                        <div className="blog-details">

                            {!loading && data?.blogPost &&
                              <>
                                <h1 className='blog-details__title'>{data.blogPost.title}</h1>
                                <span className="blog-details__date"><T id="blog.published"/> {data.blogPost.update_time}</span>
                                  <div>
                                    <span className="blog-details__category">
                                        <T id="blog.category"/>
                                        <Link
                                          className="blog-details__category-link"
                                          to={"/blog"}>
                                            {data.blogPost.__typename}
                                        </Link>
                                    </span>
                                  </div>
                              </>
                            }

                            {!loading && data?.blogPost?.tags &&
                            data.blogPost.tags.length > 0 &&
                                <div className="blog-details__tags">
                                    <span className="blog-details__tags-text"><T id="blog.tag"/></span>
                                    {data.blogPost.tags.map(({title, identifier}) => (
                                        <span key={title}>
                                            <Link className="blog-details__tags-link" to={`/blog/tag/${identifier}`}>{title}</Link>, </span>
                                    ))}
                                </div>
                            }

                            {!loading && data?.blogPost &&
                                <div className='blog-details__content'>
                                    {parse(data.blogPost.content, {replace: replaceBlogContentNode})}
                                </div>
                            }

                            {!loading && data?.blogPost?.related_posts &&
                                data.blogPost.related_posts.length > 0 &&
                            <div className="blog-details__related-posts">
                                <h3 className="blog-details__related-posts__title"><T id="blog.related"/></h3>
                                <hr/>

                                {data.blogPost.related_posts.map(({title, identifier}) => (
                                    <Link className="blog-details__related-posts__link" key={title}
                                          to={identifier}>{title}</Link>
                                ))}
                            </div>}

                        </div>
                    </div>

                    <div className="blog__column-sidebar">
                        <h3><T id="blog.newestPost"/></h3>
                        {!load && latestPostsData?.latestBlogPosts?.map(({title, identifier, first_image, update_time, order}) => (
                            <NewestBlogItem
                                key={identifier}
                                title={title}
                                url={identifier}
                                image_src={first_image}
                                creation_date={update_time}
                                order={order}
                            />
                        ))}

                        <div className="blog__column-sidebar__tags">
                            <h2><T id="blog.tags"/></h2>
                            {!lo && blogTagsData?.blogTags?.map(({identifier, title}) => (
                                <Link key={identifier} to={`/blog/tag/${identifier}`}>#{title}</Link>
                                ))}
                        </div>
                    </div>

                </div>

            </main>
        </div>
    </Fragment>

}

export default BlogDetails;
